import { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { reducedMotion } from './useLenis.js';

export function maskLines(el, vars = {}) {
  const split = new SplitText(el, {
    type: 'lines',
    linesClass: 'line-mask-inner',
    mask: 'lines',
  });

  const revert = () => split.revert();

  const tween = gsap.from(split.lines, {
    yPercent: 115,
    duration: 1,
    ease: 'power3.out',
    stagger: 0.09,
    ...vars,
    onComplete: () => {
      revert();
      vars.onComplete?.();
    },
  });

  return { tween, revert };
}

function initHeadings(scope) {
  const heads = scope.querySelectorAll('[data-reveal="lines"]');
  heads.forEach((el) => {
    ScrollTrigger.create({
      trigger: el,
      start: 'top 85%',
      once: true,
      onEnter: () => maskLines(el, { duration: 0.95 }),
    });
  });
}

function initFades(scope) {
  const items = scope.querySelectorAll('[data-reveal="fade"]');
  items.forEach((el) => {
    gsap.from(el, {
      opacity: 0,
      y: 18,
      duration: 0.8,
      ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 88%', once: true },
    });
  });
}

function initGroups(scope) {
  const groups = scope.querySelectorAll('[data-reveal="group"]');
  groups.forEach((group) => {
    gsap.from(Array.from(group.children), {
      opacity: 0,
      y: 26,
      duration: 0.7,
      ease: 'power2.out',
      stagger: 0.07,
      scrollTrigger: { trigger: group, start: 'top 85%', once: true },
    });
  });
}

function initSpine(scope) {
  scope.querySelectorAll('.spine').forEach((el) => {
    gsap.fromTo(
      el,
      { '--spine-draw': 0 },
      {
        '--spine-draw': 1,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top 75%',
          end: 'bottom bottom',
          scrub: 0.6,
        },
      }
    );
  });
}

function initParallax(scope) {
  scope.querySelectorAll('[data-parallax]').forEach((el) => {
    const distance = Number(el.dataset.parallax) || 12;
    gsap.fromTo(
      el,
      { yPercent: -distance / 2, scale: 1.12 },
      {
        yPercent: distance / 2,
        ease: 'none',
        scrollTrigger: {
          trigger: el.parentElement ?? el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      }
    );
  });
}

function initCounters(scope) {
  scope.querySelectorAll('[data-count]').forEach((el) => {
    const target = Number(el.dataset.count) || 0;
    const state = { value: 0 };

    ScrollTrigger.create({
      trigger: el,
      start: 'top 88%',
      once: true,
      onEnter: () => {
        gsap.to(state, {
          value: target,
          duration: 1.8,
          ease: 'power2.out',
          onUpdate: () => {
            el.textContent = String(Math.round(state.value));
          },
        });
      },
    });
  });
}

function initHorizontal(scope) {
  const viewports = scope.querySelectorAll('[data-horizontal]');
  if (!viewports.length) return null;

  const mm = gsap.matchMedia();
  mm.add('(min-width: 1024px)', () => {
    viewports.forEach((viewport) => {
      const track = viewport.querySelector('[data-horizontal-track]');
      if (!track) return;

      const distance = () => track.scrollWidth - viewport.clientWidth;
      if (distance() <= 0) return;

      const splitCards = track.querySelectorAll('[data-split-card]');

      if (splitCards.length > 0) {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: viewport,
            start: 'center center',
            end: () => `+=${distance() * 1.2}`,
            pin: true,
            scrub: 0.8,
            invalidateOnRefresh: true,
            anticipatePin: 1,
          },
        });

        tl.to(track, {
          x: () => -distance(),
          ease: 'none',
          duration: 1,
        });

        const count = splitCards.length;
        splitCards.forEach((card, index) => {
          const top = card.querySelector('.work-split-top');
          const bottom = card.querySelector('.work-split-bottom');
          if (!top || !bottom) return;

          const startTime = count === 1 ? 0.05 : (index / count) * 0.72 + 0.04;
          const splitDuration = 0.22;

          tl.to(
            top,
            {
              yPercent: -102,
              ease: 'power1.inOut',
              duration: splitDuration,
            },
            startTime
          );

          tl.to(
            bottom,
            {
              yPercent: 102,
              ease: 'power1.inOut',
              duration: splitDuration,
            },
            startTime
          );
        });
      } else {
        gsap.to(track, {
          x: () => -distance(),
          ease: 'none',
          scrollTrigger: {
            trigger: viewport,
            start: 'center center',
            end: () => `+=${distance()}`,
            pin: true,
            scrub: 0.8,
            invalidateOnRefresh: true,
            anticipatePin: 1,
          },
        });
      }
    });
  });

  return mm;
}

function initChars(scope) {
  scope.querySelectorAll('[data-reveal="chars"]').forEach((el) => {
    ScrollTrigger.create({
      trigger: el,
      start: 'top 88%',
      once: true,
      onEnter: () => {
        const split = new SplitText(el, { type: 'chars,lines', mask: 'lines' });
        gsap.from(split.chars, {
          yPercent: 120,
          opacity: 0,
          filter: 'blur(10px)',
          duration: 0.9,
          ease: 'power3.out',
          stagger: { each: 0.016, from: 'start' },
        });
      },
    });
  });
}

function initScaleOnScroll(scope) {
  scope.querySelectorAll('[data-scale]').forEach((el) => {
    gsap.fromTo(
      el,
      { scale: 0.88, borderRadius: '2rem' },
      {
        scale: 1,
        borderRadius: '0rem',
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top 92%',
          end: 'top 38%',
          scrub: 0.8,
        },
      }
    );
  });
}

function initSweeps(scope) {
  scope.querySelectorAll('.sweep').forEach((el) => {
    gsap.fromTo(
      el,
      { backgroundPosition: '120% 0' },
      {
        backgroundPosition: '-20% 0',
        duration: 1.6,
        ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 90%', once: true },
      }
    );
  });
}

function initProgress(scope) {
  const bar = scope.querySelector('[data-progress]');
  if (!bar) return;

  gsap.to(bar, {
    scaleX: 1,
    ease: 'none',
    scrollTrigger: { start: 0, end: 'max', scrub: 0.3 },
  });
}

function initVideoReveal(scope) {
  scope.querySelectorAll('[data-video-reveal]').forEach((el) => {
    gsap.fromTo(
      el,
      { clipPath: 'inset(12% 14% round 0px)' },
      {
        clipPath: 'inset(0% 0% round 0px)',
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          end: 'top 25%',
          scrub: 0.7,
        },
      }
    );
  });
}

function initVideoPlayback(scope) {
  scope.querySelectorAll('video[data-autoplay]').forEach((video) => {
    ScrollTrigger.create({
      trigger: video,
      start: 'top bottom',
      end: 'bottom top',
      onToggle: (self) => {
        if (self.isActive) {
          void video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
    });
  });
}

function settleCounters(scope) {
  scope.querySelectorAll('[data-count]').forEach((el) => {
    el.textContent = el.dataset.count ?? el.textContent;
  });
}

export function useMotion(scopeRef) {
  useEffect(() => {
    const scope = scopeRef?.current ?? document;

    initProgress(scope);
    initVideoPlayback(scope);

    if (reducedMotion()) {
      settleCounters(scope);
      return;
    }

    initSpine(scope);
    initChars(scope);
    initScaleOnScroll(scope);
    initSweeps(scope);
    initHeadings(scope);
    initFades(scope);
    initGroups(scope);
    initParallax(scope);
    initCounters(scope);
    const mmHorizontal = initHorizontal(scope);
    initVideoReveal(scope);

    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);

    return () => {
      clearTimeout(refreshTimer);
      mmHorizontal?.revert();
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger && scope.contains(st.trigger)) {
          st.kill();
        }
      });
    };
  }, [scopeRef]);
}